// Three.js Renderer for 4D Tetris

import * as THREE from 'three';
import { GridConfig } from '../game/Grid4D';

interface BlockData {
  x: number;
  y: number;
  z: number;
  w: number;
  color: number;
}

export class Renderer {
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private renderer: THREE.WebGLRenderer;

  // Object groups
  private gridGroup: THREE.Group;
  private blocksGroup: THREE.Group;
  private activeGroup: THREE.Group;
  private ghostGroup: THREE.Group;
  private pitGroup: THREE.Group;
  private particleGroup: THREE.Group;

  // Materials cache
  private blockMaterials: Map<number, THREE.MeshPhysicalMaterial> = new Map();
  private ghostMaterial: THREE.MeshPhysicalMaterial;
  private pitMaterial: THREE.MeshPhysicalMaterial;

  // Geometry cache
  private blockGeometry: THREE.BoxGeometry;

  // Animation
  private time: number = 0;
  private currentW: number = 1;
  private gridConfig: GridConfig;

  // Effects
  private particles: THREE.Points[] = [];

  constructor(container: HTMLElement, gridConfig: GridConfig) {
    this.gridConfig = gridConfig;

    // Scene setup
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x050510);
    this.scene.fog = new THREE.FogExp2(0x050510, 0.02);

    // Camera setup - looking down into the pit
    this.camera = new THREE.PerspectiveCamera(
      60,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    this.camera.position.set(0, -8, 12);
    this.camera.lookAt(0, 4, 0);

    // Renderer setup with WebGL2
    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      powerPreference: 'high-performance',
    });
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.2;
    container.appendChild(this.renderer.domElement);

    // Groups
    this.gridGroup = new THREE.Group();
    this.blocksGroup = new THREE.Group();
    this.activeGroup = new THREE.Group();
    this.ghostGroup = new THREE.Group();
    this.pitGroup = new THREE.Group();
    this.particleGroup = new THREE.Group();

    this.scene.add(this.gridGroup);
    this.scene.add(this.blocksGroup);
    this.scene.add(this.activeGroup);
    this.scene.add(this.ghostGroup);
    this.scene.add(this.pitGroup);
    this.scene.add(this.particleGroup);

    // Geometry (shared)
    this.blockGeometry = new THREE.BoxGeometry(0.9, 0.9, 0.9);

    // Materials
    this.ghostMaterial = new THREE.MeshPhysicalMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.15,
      roughness: 0.1,
      metalness: 0.5,
      side: THREE.DoubleSide,
    });

    this.pitMaterial = new THREE.MeshPhysicalMaterial({
      color: 0x1a1a2e,
      transparent: true,
      opacity: 0.3,
      roughness: 0.5,
      metalness: 0.8,
      side: THREE.BackSide,
    });

    this.setupLights();
    this.createPits();
    this.createGrid();
    this.createStarfield();
    this.createAmbientParticles();

    // Handle resize
    window.addEventListener('resize', this.onResize.bind(this));
  }

  private setupLights(): void {
    // Ambient light
    const ambient = new THREE.AmbientLight(0x404060, 0.5);
    this.scene.add(ambient);

    // Main directional light (from above)
    const mainLight = new THREE.DirectionalLight(0xffffff, 1);
    mainLight.position.set(5, -10, 10);
    mainLight.castShadow = true;
    mainLight.shadow.mapSize.width = 2048;
    mainLight.shadow.mapSize.height = 2048;
    mainLight.shadow.camera.near = 0.5;
    mainLight.shadow.camera.far = 50;
    this.scene.add(mainLight);

    // Colored accent lights
    const cyanLight = new THREE.PointLight(0x00f7ff, 2, 30);
    cyanLight.position.set(-10, 0, 5);
    this.scene.add(cyanLight);

    const magentaLight = new THREE.PointLight(0xff00f7, 2, 30);
    magentaLight.position.set(10, 0, 5);
    this.scene.add(magentaLight);

    // Rim light from below
    const rimLight = new THREE.PointLight(0x4444ff, 1, 20);
    rimLight.position.set(0, 15, 0);
    this.scene.add(rimLight);
  }

  private createPits(): void {
    const { sizeX, sizeY, sizeZ, sizeW } = this.gridConfig;
    const pitSpacing = sizeX + 3;

    for (let w = 0; w < sizeW; w++) {
      const offsetX = (w - (sizeW - 1) / 2) * pitSpacing;

      // Pit walls (glass-like)
      const pitGeometry = new THREE.BoxGeometry(sizeX + 0.5, sizeY + 0.5, sizeZ + 0.5);
      const pitMesh = new THREE.Mesh(pitGeometry, this.pitMaterial.clone());
      pitMesh.position.set(
        offsetX + (sizeX - 1) / 2,
        (sizeY - 1) / 2,
        (sizeZ - 1) / 2
      );
      pitMesh.userData.w = w;
      this.pitGroup.add(pitMesh);

      // Pit floor (reflective)
      const floorGeometry = new THREE.PlaneGeometry(sizeX + 1, sizeZ + 1);
      const floorMaterial = new THREE.MeshPhysicalMaterial({
        color: 0x1a1a3e,
        metalness: 0.9,
        roughness: 0.1,
        envMapIntensity: 1,
      });
      const floor = new THREE.Mesh(floorGeometry, floorMaterial);
      floor.rotation.x = -Math.PI / 2;
      floor.position.set(
        offsetX + (sizeX - 1) / 2,
        sizeY + 0.3,
        (sizeZ - 1) / 2
      );
      floor.receiveShadow = true;
      this.pitGroup.add(floor);

      // Pit edge glow
      const edgeGeometry = new THREE.EdgesGeometry(pitGeometry);
      const edgeMaterial = new THREE.LineBasicMaterial({
        color: w === 1 ? 0x00f7ff : 0x444466,
        linewidth: 2,
      });
      const edges = new THREE.LineSegments(edgeGeometry, edgeMaterial);
      edges.position.copy(pitMesh.position);
      edges.userData.w = w;
      this.pitGroup.add(edges);
    }
  }

  private createGrid(): void {
    const { sizeX, sizeY, sizeZ, sizeW } = this.gridConfig;
    const pitSpacing = sizeX + 3;

    // Grid lines for each pit
    for (let w = 0; w < sizeW; w++) {
      const offsetX = (w - (sizeW - 1) / 2) * pitSpacing;
      const gridMaterial = new THREE.LineBasicMaterial({
        color: 0x333355,
        transparent: true,
        opacity: 0.3,
      });

      const points: THREE.Vector3[] = [];

      // Vertical lines (along Y)
      for (let x = 0; x <= sizeX; x++) {
        for (let z = 0; z <= sizeZ; z++) {
          points.push(new THREE.Vector3(offsetX + x - 0.5, -0.5, z - 0.5));
          points.push(new THREE.Vector3(offsetX + x - 0.5, sizeY - 0.5, z - 0.5));
        }
      }

      // Horizontal lines (along X)
      for (let y = 0; y <= sizeY; y++) {
        for (let z = 0; z <= sizeZ; z++) {
          points.push(new THREE.Vector3(offsetX - 0.5, y - 0.5, z - 0.5));
          points.push(new THREE.Vector3(offsetX + sizeX - 0.5, y - 0.5, z - 0.5));
        }
      }

      // Depth lines (along Z)
      for (let x = 0; x <= sizeX; x++) {
        for (let y = 0; y <= sizeY; y++) {
          points.push(new THREE.Vector3(offsetX + x - 0.5, y - 0.5, -0.5));
          points.push(new THREE.Vector3(offsetX + x - 0.5, y - 0.5, sizeZ - 0.5));
        }
      }

      const geometry = new THREE.BufferGeometry().setFromPoints(points);
      const grid = new THREE.LineSegments(geometry, gridMaterial);
      grid.userData.w = w;
      this.gridGroup.add(grid);
    }
  }

  private createStarfield(): void {
    const starGeometry = new THREE.BufferGeometry();
    const starCount = 2000;
    const positions = new Float32Array(starCount * 3);
    const colors = new Float32Array(starCount * 3);

    for (let i = 0; i < starCount; i++) {
      const i3 = i * 3;
      positions[i3] = (Math.random() - 0.5) * 200;
      positions[i3 + 1] = (Math.random() - 0.5) * 200;
      positions[i3 + 2] = (Math.random() - 0.5) * 200 - 50;

      const color = new THREE.Color();
      color.setHSL(Math.random() * 0.2 + 0.5, 0.8, 0.8);
      colors[i3] = color.r;
      colors[i3 + 1] = color.g;
      colors[i3 + 2] = color.b;
    }

    starGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    starGeometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    const starMaterial = new THREE.PointsMaterial({
      size: 0.5,
      vertexColors: true,
      transparent: true,
      opacity: 0.8,
    });

    const stars = new THREE.Points(starGeometry, starMaterial);
    this.scene.add(stars);
  }

  private createAmbientParticles(): void {
    const particleCount = 100;
    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(particleCount * 3);

    for (let i = 0; i < particleCount; i++) {
      const i3 = i * 3;
      positions[i3] = (Math.random() - 0.5) * 30;
      positions[i3 + 1] = Math.random() * 20 - 5;
      positions[i3 + 2] = (Math.random() - 0.5) * 30;
    }

    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

    const material = new THREE.PointsMaterial({
      color: 0x00f7ff,
      size: 0.1,
      transparent: true,
      opacity: 0.5,
    });

    const particles = new THREE.Points(geometry, material);
    this.particleGroup.add(particles);
    this.particles.push(particles);
  }

  private getBlockMaterial(color: number): THREE.MeshPhysicalMaterial {
    if (!this.blockMaterials.has(color)) {
      const material = new THREE.MeshPhysicalMaterial({
        color: color,
        metalness: 0.3,
        roughness: 0.2,
        clearcoat: 1.0,
        clearcoatRoughness: 0.1,
        envMapIntensity: 1.5,
        emissive: color,
        emissiveIntensity: 0.1,
      });
      this.blockMaterials.set(color, material);
    }
    return this.blockMaterials.get(color)!;
  }

  private createBlockMesh(block: BlockData, material: THREE.Material): THREE.Mesh {
    const mesh = new THREE.Mesh(this.blockGeometry, material);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    this.positionBlock(mesh, block);
    return mesh;
  }

  private positionBlock(mesh: THREE.Mesh, block: BlockData): void {
    const pitSpacing = this.gridConfig.sizeX + 3;
    const offsetX = (block.w - (this.gridConfig.sizeW - 1) / 2) * pitSpacing;

    mesh.position.set(
      offsetX + block.x,
      block.y,
      block.z
    );
  }

  setCurrentW(w: number): void {
    this.currentW = w;

    // Update pit highlighting
    this.pitGroup.children.forEach(child => {
      if (child instanceof THREE.LineSegments) {
        const edgeMaterial = child.material as THREE.LineBasicMaterial;
        edgeMaterial.color.setHex(child.userData.w === w ? 0x00f7ff : 0x444466);
      }
      if (child instanceof THREE.Mesh && child.userData.w !== undefined) {
        const pitMaterial = child.material as THREE.MeshPhysicalMaterial;
        pitMaterial.opacity = child.userData.w === w ? 0.4 : 0.2;
      }
    });
  }

  updateBlocks(
    gridBlocks: BlockData[],
    activeBlocks: BlockData[],
    ghostBlocks: BlockData[]
  ): void {
    // Clear existing
    this.blocksGroup.clear();
    this.activeGroup.clear();
    this.ghostGroup.clear();

    // Add grid blocks
    for (const block of gridBlocks) {
      const material = this.getBlockMaterial(block.color);
      const mesh = this.createBlockMesh(block, material);

      // Fade based on distance from current W
      const wDistance = Math.abs(block.w - this.currentW);
      if (wDistance > 0) {
        const fadeMaterial = material.clone();
        fadeMaterial.transparent = true;
        fadeMaterial.opacity = 0.3 / wDistance;
        mesh.material = fadeMaterial;
      }

      this.blocksGroup.add(mesh);
    }

    // Add active piece blocks
    for (const block of activeBlocks) {
      const material = this.getBlockMaterial(block.color);
      const mesh = this.createBlockMesh(block, material);

      // Pulse effect for active piece
      const scale = 1 + Math.sin(this.time * 5) * 0.02;
      mesh.scale.setScalar(scale);

      // Fade for non-current W slices
      const wDistance = Math.abs(block.w - this.currentW);
      if (wDistance > 0) {
        const fadeMaterial = material.clone();
        fadeMaterial.transparent = true;
        fadeMaterial.opacity = 0.5 / wDistance;
        fadeMaterial.emissiveIntensity = 0.3;
        mesh.material = fadeMaterial;
      } else {
        // Current slice gets extra glow
        const glowMaterial = material.clone();
        glowMaterial.emissiveIntensity = 0.3;
        mesh.material = glowMaterial;
      }

      this.activeGroup.add(mesh);
    }

    // Add ghost blocks (only in current W)
    for (const block of ghostBlocks) {
      if (block.w === this.currentW) {
        const mesh = this.createBlockMesh(block, this.ghostMaterial);
        this.ghostGroup.add(mesh);
      }
    }
  }

  // Particle explosion effect when lines clear
  createLinesClearEffect(y: number): void {
    const particleCount = 50;
    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(particleCount * 3);
    const velocities: THREE.Vector3[] = [];

    for (let i = 0; i < particleCount; i++) {
      const i3 = i * 3;
      positions[i3] = (Math.random() - 0.5) * this.gridConfig.sizeX;
      positions[i3 + 1] = y;
      positions[i3 + 2] = (Math.random() - 0.5) * this.gridConfig.sizeZ;

      velocities.push(
        new THREE.Vector3(
          (Math.random() - 0.5) * 2,
          (Math.random() - 0.5) * 2,
          (Math.random() - 0.5) * 2
        )
      );
    }

    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

    const material = new THREE.PointsMaterial({
      color: 0x00f7ff,
      size: 0.3,
      transparent: true,
      opacity: 1,
    });

    const particles = new THREE.Points(geometry, material);
    particles.userData.velocities = velocities;
    particles.userData.life = 1;
    this.particleGroup.add(particles);
  }

  animate(deltaTime: number): void {
    this.time += deltaTime;

    // Animate ambient particles
    for (const particles of this.particles) {
      const positions = particles.geometry.attributes.position.array as Float32Array;
      for (let i = 0; i < positions.length; i += 3) {
        positions[i + 1] += deltaTime * 0.5;
        if (positions[i + 1] > 20) {
          positions[i + 1] = -5;
        }
      }
      particles.geometry.attributes.position.needsUpdate = true;
    }

    // Animate explosion particles
    const toRemove: THREE.Points[] = [];
    this.particleGroup.children.forEach(child => {
      if (child instanceof THREE.Points && child.userData.life !== undefined) {
        child.userData.life -= deltaTime * 2;
        if (child.userData.life <= 0) {
          toRemove.push(child);
        } else {
          const positions = child.geometry.attributes.position.array as Float32Array;
          const velocities = child.userData.velocities as THREE.Vector3[];
          for (let i = 0; i < positions.length / 3; i++) {
            const i3 = i * 3;
            positions[i3] += velocities[i].x * deltaTime;
            positions[i3 + 1] += velocities[i].y * deltaTime;
            positions[i3 + 2] += velocities[i].z * deltaTime;
            velocities[i].y -= deltaTime * 5; // gravity
          }
          child.geometry.attributes.position.needsUpdate = true;
          (child.material as THREE.PointsMaterial).opacity = child.userData.life;
        }
      }
    });
    toRemove.forEach(p => this.particleGroup.remove(p));

    // Slight camera bob
    this.camera.position.x = Math.sin(this.time * 0.3) * 0.3;
    this.camera.position.z = 12 + Math.cos(this.time * 0.2) * 0.2;
  }

  render(): void {
    this.renderer.render(this.scene, this.camera);
  }

  private onResize(): void {
    const width = window.innerWidth;
    const height = window.innerHeight;

    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();

    this.renderer.setSize(width, height);
  }

  dispose(): void {
    window.removeEventListener('resize', this.onResize.bind(this));
    this.renderer.dispose();
    this.blockGeometry.dispose();
    this.blockMaterials.forEach(m => m.dispose());
    this.ghostMaterial.dispose();
    this.pitMaterial.dispose();
  }
}
